import "./App.css";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  Building2, FolderKanban, Activity, Lock,
} from "lucide-react";

// ── Data & Config ──
import { FIXED_ROLES, ROLE_PAGES, ROLE_EDITS, HardHat } from "./data/roles";
import { requestNotificationPermission, shouldAskPermission, showNewNotifications, subscribeToPush, sendPushToUsers } from "./utils/browserNotifications";

// ── Context ──
import { DataContext, DataProvider, useData } from "./context/DataContext";
// useApp() from AppContext is a convenience alias for useContext(DataContext)

// ── UI Components ──
import { StatusBadge } from "./components/ui";
import { ErrorBoundary } from "./components/ui/ErrorBoundary";
import { inputClass } from "./styles/classes";

// ── Layout ──
import { Sidebar } from "./components/layout/Sidebar";
import { LoginScreen } from "./components/layout/LoginScreen";
import { AppRouter } from "./AppRouter";
import { TopBar } from "./components/layout/TopBar";

// ── Setup ──
import { SeedPage } from "./features/setup/SeedPage";

// ── Toast ──
import { ToastProvider } from "./context/ToastContext";

// ─── APP WRAPPER (provides DataProvider) ──────────────────────────────
export default function App() {
  // Show seed page at #/setup (no auth required)
  const [hash, setHash] = useState(window.location.hash);
  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  if (hash === "#/setup") return <SeedPage />;

  return (
    <DataProvider>
      <AppShell />
    </DataProvider>
  );
}

// ─── APP SHELL (consumes useData) ─────────────────────────────────────

function AppShell() {
  const { data, setData, session, loading, signOut, loginLocal } = useData();

  // ── UI State ──
  const [page, setPage] = useState("dashboard");
  const [selectedProject, setSelectedProject] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentRole, setCurrentRole] = useState("ceo");
  const [currentUserId, setCurrentUserId] = useState("u1");
  const [currentCompanyId, setCurrentCompanyId] = useState("c1");
  const [theme, setTheme] = useState(() => localStorage.getItem("ametal_pcs_theme") || "dark");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("ametal_pcs_theme", theme);
  }, [theme]);

  const toggleTheme = useCallback(() => setTheme(t => t === "dark" ? "light" : "dark"), []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Navigation history (back/forward)
  const historyStack = useRef(["dashboard"]);
  const historyIdx = useRef(0);
  const isNavAction = useRef(false);

  // Load Google Fonts for local development
  useEffect(() => {
    if (!document.querySelector('link[href*="fonts.googleapis"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=JetBrains+Mono:wght@400;500&display=swap";
      document.head.appendChild(l);
    }
  }, []);

  // Build dynamic roles list — PMs + Supervisors per project
  const pmEntries = (data?.projects || []).filter(p => p.project_manager_id && p.status !== "Closed").map(p => {
    const pmUser = data.users.find(u => u.user_id === p.project_manager_id);
    return {
      id: `pm_${p.project_id}`,
      label: `PM: ${pmUser?.name || "Unknown"}`,
      desc: p.name,
      sublabel: p.name,
      user: p.project_manager_id,
      icon: Activity,
      color: "#ec4899",
      home: "controlcenter",
      role: "pm",
    };
  });
  const svEntries = (data?.projects || []).filter(p => p.supervisor_id && p.status !== "Closed").map(p => {
    const svUser = data.users.find(u => u.user_id === p.supervisor_id);
    return {
      id: `sv_${p.project_id}`,
      label: `Supervisor: ${svUser?.name || "Unknown"}`,
      desc: p.name,
      sublabel: p.name,
      user: p.supervisor_id,
      icon: HardHat,
      color: "#f97316",
      home: "supervisorcc",
      role: "supervisor",
    };
  });
  const ROLES = [...FIXED_ROLES.slice(0, 2), ...pmEntries, ...svEntries, ...FIXED_ROLES.slice(2)];

  const currentUser = data?.users?.find(u => u.user_id === currentUserId) || data?.users?.[0];
  const currentRoleLabel = currentRole === "pm" ? `PM: ${currentUser?.name || ""}` : currentRole === "supervisor" ? `Supervisor: ${currentUser?.name || ""}` : FIXED_ROLES.find(r => r.role === currentRole)?.label || currentRole;

  const currentCompany = (data?.companies || []).find(c => c.company_id === currentCompanyId) || data?.companies?.[0];

  const navigateTo = useCallback((id) => {
    if (!isNavAction.current) {
      historyStack.current = historyStack.current.slice(0, historyIdx.current + 1);
      historyStack.current.push(id);
      historyIdx.current = historyStack.current.length - 1;
    }
    isNavAction.current = false;
    setPage(id);
    if (window.innerWidth < 1024) setSidebarOpen(false);
  }, []);

  const canGoBack = historyIdx.current > 0;
  const canGoForward = historyIdx.current < historyStack.current.length - 1;

  const goBack = useCallback(() => {
    if (historyIdx.current > 0) { historyIdx.current -= 1; isNavAction.current = true; navigateTo(historyStack.current[historyIdx.current]); }
  }, [navigateTo]);

  const goForward = useCallback(() => {
    if (historyIdx.current < historyStack.current.length - 1) { historyIdx.current += 1; isNavAction.current = true; navigateTo(historyStack.current[historyIdx.current]); }
  }, [navigateTo]);

  // ── Role Access ──
  const allowedPages = ROLE_PAGES[currentRole];
  const canAccess = (pageId) => !allowedPages || allowedPages.includes(pageId) || pageId.startsWith("mobile");
  const canEdit = (pageId) => ROLE_EDITS[pageId]?.includes(currentRole) || currentRole === "ceo" || currentRole === "admin";

  const isMobilePage = (p) => p.startsWith("mobile");

  const markNotifRead = (id) => setData(d => ({ ...d, notifications: d.notifications.map(n => n.id === id ? { ...n, is_read: true } : n) }));
  const isExecRole = ["CEO", "COO", "Admin"].includes(currentRole);
  const markAllRead = () => setData(d => ({ ...d, notifications: d.notifications.map(n => (isExecRole || n.to_user_id === currentUserId) ? { ...n, is_read: true } : n) }));
  const toggleTodoStatus = (id) => setData(d => ({ ...d, todos: d.todos.map(t => t.id === id ? { ...t, status: t.status === "pending" ? "done" : "pending", completed_at: t.status === "pending" ? new Date().toISOString() : null } : t) }));
  const addTodo = (todo) => {
    const assignTo = todo.assign_to || currentUserId;
    const newTodo = { id: `todo_${Date.now()}`, title: todo.title, assigned_to: assignTo, assigned_by: currentUserId, priority: todo.priority || "normal", due_date: todo.due_date || null, status: "pending", created_at: new Date().toISOString(), completed_at: null };
    setData(d => {
      const notifs = (assignTo !== currentUserId) ? [...d.notifications, { id: `notif_${Date.now()}`, type: "todo", to_user_id: assignTo, title: `New task: ${todo.title}`, message: `Assigned by ${currentUser?.name || ""}`, page_link: "mobiletodos", is_read: false, created_at: new Date().toISOString() }] : d.notifications;
      return { ...d, todos: [...d.todos, newTodo], notifications: notifs };
    });
  };
  const deleteTodo = (id) => setData(d => ({ ...d, todos: d.todos.filter(t => t.id !== id) }));

  // ── Browser / phone notification watcher ──
  const prevNotifsRef = useRef(null);
  useEffect(() => {
    if (!isLoggedIn || !data?.notifications) return;
    // Request permission + subscribe to push on first login
    if (shouldAskPermission()) {
      setTimeout(async () => {
        const perm = await requestNotificationPermission();
        if (perm === "granted") {
          subscribeToPush(currentUserId, currentCompanyId);
        }
      }, 3000);
    } else {
      // Already granted — ensure push subscription is registered
      subscribeToPush(currentUserId, currentCompanyId);
    }
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLoggedIn || !data?.notifications) return;
    if (prevNotifsRef.current) {
      showNewNotifications(prevNotifsRef.current, data.notifications, currentUserId, isExecRole);
    }
    prevNotifsRef.current = data.notifications;
  }, [data?.notifications, currentUserId, isExecRole, isLoggedIn]);

  // Handle service worker navigation messages (notification click)
  useEffect(() => {
    if (!navigator.serviceWorker) return;
    const handler = (event) => {
      if (event.data?.type === "NAVIGATE" && event.data.page) {
        navigateTo(event.data.page);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [navigateTo]);

  // Notes handlers
  const saveNote = (note) => {
    const now = new Date().toISOString();
    setData(d => {
      const notes = d.userNotes || [];
      if (note.note_id && note.note_id !== "new") {
        // Update existing — merge only provided fields
        return { ...d, userNotes: notes.map(n => n.note_id === note.note_id ? { ...n, ...note, updated_at: now } : n) };
      }
      // Create new
      const newNote = { note_id: `note_${Date.now()}`, user_id: currentUserId, title: note.title || "", content: note.content || "", color: note.color || "#0ea5e9", is_pinned: false, created_at: now, updated_at: now };
      return { ...d, userNotes: [...notes, newNote] };
    });
  };
  const deleteNote = (noteId) => setData(d => ({ ...d, userNotes: (d.userNotes || []).filter(n => n.note_id !== noteId) }));

  // ── Trust this browser + midnight auto-signout ──
  const [showTrustPrompt, setShowTrustPrompt] = useState(false);
  const pendingLogin = useRef(null); // { sel, matchedUser, updateLastLogin }

  // Get midnight tonight in local time
  const getMidnight = () => {
    const d = new Date();
    d.setHours(24, 0, 0, 0);
    return d.getTime();
  };

  // Schedule auto-signout at midnight
  const midnightTimer = useRef(null);
  const scheduleMidnightSignout = useCallback(() => {
    if (midnightTimer.current) clearTimeout(midnightTimer.current);
    const ms = getMidnight() - Date.now();
    midnightTimer.current = setTimeout(() => {
      localStorage.removeItem("ametal_pcs_trust");
      setIsLoggedIn(false);
      signOut();
    }, ms);
  }, [signOut]);

  // On mount: check for trusted session and auto-login
  useEffect(() => {
    if (isLoggedIn || !data) return;
    try {
      const trust = JSON.parse(localStorage.getItem("ametal_pcs_trust") || "null");
      if (trust && trust.expiry > Date.now()) {
        // Restore session from trust token
        setCurrentRole(trust.role);
        setCurrentUserId(trust.userId);
        if (trust.companyId) setCurrentCompanyId(trust.companyId);
        const fullAccessRoles = ["ceo", "coo", "admin"];
        const homePage = (isMobile && !fullAccessRoles.includes(trust.role)) ? "mobilehome" : (trust.home || "dashboard");
        setPage(homePage);
        historyStack.current = [homePage];
        historyIdx.current = 0;
        setIsLoggedIn(true);
        if (trust.role === "pm" && trust.roleId) setSelectedProject(trust.roleId.replace("pm_", ""));
        if (trust.role === "supervisor" && trust.roleId) setSelectedProject(trust.roleId.replace("sv_", ""));
        // Also restore data from localStorage if needed (local auth path)
        if (!session) {
          const user = data.users?.find(u => u.user_id === trust.userId);
          if (user) loginLocal(user.email);
        }
        scheduleMidnightSignout();
      } else {
        localStorage.removeItem("ametal_pcs_trust");
      }
    } catch {
      localStorage.removeItem("ametal_pcs_trust");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Finalize login (called after trust decision or skip)
  const finalizeLogin = useCallback((sel, matchedUser, updateLastLogin) => {
    setCurrentRole(sel.role);
    setCurrentUserId(sel.user);
    if (matchedUser?.company_id) setCurrentCompanyId(matchedUser.company_id);
    const fullAccessRoles = ["ceo", "coo", "admin"];
    const homePage = (isMobile && !fullAccessRoles.includes(sel.role)) ? "mobilehome" : (sel.home || "dashboard");
    setPage(homePage);
    historyStack.current = [homePage];
    historyIdx.current = 0;
    setIsLoggedIn(true);
    if (sel.role === "pm") setSelectedProject(sel.id.replace("pm_", ""));
    if (sel.role === "supervisor") setSelectedProject(sel.id.replace("sv_", ""));
    if (updateLastLogin) {
      setData(d => ({ ...d, users: d.users.map(u => u.user_id === sel.user ? { ...u, last_login: new Date().toISOString() } : u) }));
    }
    scheduleMidnightSignout();
  }, [isMobile, setData, scheduleMidnightSignout]);

  // ── Shared login handler factory ──
  const createLoginHandler = useCallback(({ updateLastLogin = false } = {}) => (sel, matchedUser) => {
    // Store pending login and show trust prompt
    pendingLogin.current = { sel, matchedUser, updateLastLogin };
    setShowTrustPrompt(true);
  }, []);

  const handleTrustDecision = useCallback((trusted) => {
    const { sel, matchedUser, updateLastLogin } = pendingLogin.current;
    if (trusted) {
      localStorage.setItem("ametal_pcs_trust", JSON.stringify({
        userId: sel.user,
        role: sel.role,
        roleId: sel.id,
        home: sel.home || "dashboard",
        companyId: matchedUser?.company_id || "",
        expiry: getMidnight(),
      }));
    }
    setShowTrustPrompt(false);
    pendingLogin.current = null;
    finalizeLogin(sel, matchedUser, updateLastLogin);
  }, [finalizeLogin]);

  // ── Loading state ──
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a0c10", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: "linear-gradient(135deg, #0ea5e9, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", animation: "pulse 1.5s ease-in-out infinite" }}>
            <Building2 size={24} color="#fff" />
          </div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>Loading...</div>
          <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }`}</style>
        </div>
      </div>
    );
  }

  // ── Trust Browser Prompt ──
  const trustPromptModal = showTrustPrompt && (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.7)", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ background: "#1a1d24", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: "32px 28px", maxWidth: 400, width: "90%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg, #0ea5e9, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <Lock size={24} color="#fff" />
        </div>
        <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 600, margin: "0 0 8px" }}>Trust this browser?</h3>
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, margin: "0 0 24px", lineHeight: 1.5 }}>
          You won't need to sign in again today. Session will automatically expire at midnight.
        </p>
        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={() => handleTrustDecision(false)}
            style={{ flex: 1, padding: "10px 16px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)", background: "transparent", color: "rgba(255,255,255,0.6)", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}
          >
            No, ask every time
          </button>
          <button
            onClick={() => handleTrustDecision(true)}
            style={{ flex: 1, padding: "10px 16px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #0ea5e9, #2563eb)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
          >
            Yes, trust it
          </button>
        </div>
      </div>
    </div>
  );

  // ── Check if trust token is being restored (don't flash login screen) ──
  const hasTrustToken = (() => {
    try {
      const t = JSON.parse(localStorage.getItem("ametal_pcs_trust") || "null");
      return t && t.expiry > Date.now();
    } catch { return false; }
  })();

  // ── Not logged in (no Supabase session) → show LoginScreen ──
  if ((!session || !data) && !hasTrustToken) {
    return <>{trustPromptModal}<LoginScreen roles={ROLES} onLogin={createLoginHandler()} users={data?.users} companies={data?.companies} loginLocal={loginLocal} /></>;
  }

  // ── Trust token exists but still restoring → show loading ──
  if (!isLoggedIn && hasTrustToken) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a0c10", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: "linear-gradient(135deg, #0ea5e9, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", animation: "pulse 1.5s ease-in-out infinite" }}>
            <Building2 size={24} color="#fff" />
          </div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>Restoring session...</div>
          <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }`}</style>
        </div>
      </div>
    );
  }

  // ── Logged in via Supabase but haven't selected a role yet ──
  if (!isLoggedIn) {
    return <>{trustPromptModal}<LoginScreen roles={ROLES} onLogin={createLoginHandler({ updateLastLogin: true })} users={data.users} companies={data.companies} supabaseUser={session.user} /></>;
  }

  const handleSignOut = async () => {
    localStorage.removeItem("ametal_pcs_trust");
    if (midnightTimer.current) clearTimeout(midnightTimer.current);
    setIsLoggedIn(false);
    await signOut();
  };

  return (
    <ToastProvider>
    <DataContext.Provider value={{ data, setData, canEdit, currentRole, currentUser, currentCompany }}>
    <ErrorBoundary>
      <style>{`
        .apcs-root { display: flex; min-height: 100vh; position: relative; overflow: hidden; background: linear-gradient(135deg, #0a0c10 0%, #111318 50%, #0d1117 100%); font-family: 'DM Sans', sans-serif; }
        .apcs-root select option, .apcs-root select optgroup { background: #1a1d24; color: #e2e8f0; }
        .apcs-root select option:checked { background: #0ea5e9; color: #fff; }
        .apcs-root select optgroup { color: #94a3b8; font-weight: 600; }
        .apcs-overlay { display: none; }
        .apcs-sidebar { position: fixed; top: 0; left: 0; bottom: 0; width: 240px; z-index: 40; transform: translateX(-100%); transition: transform 0.25s ease, width 0.25s ease; background: #0d0f13; border-right: 1px solid rgba(255,255,255,0.06); display: flex; flex-direction: column; overflow: hidden; }
        .apcs-sidebar.open { transform: translateX(0); }
        .apcs-sidebar.open + .apcs-overlay { display: block; position: fixed; inset: 0; z-index: 30; background: rgba(0,0,0,0.6); }
        .apcs-main { flex: 1; min-width: 0; overflow-y: auto; height: 100vh; margin-left: 0; }
        .apcs-sidebar .nav-label, .apcs-sidebar .group-label, .apcs-sidebar .user-info { opacity: 1; transition: opacity 0.2s ease; }
        .apcs-mobile-topbar { display: flex; }
        .apcs-desktop-topbar { display: none; }
        .nav-btn { position: relative; }
        .nav-tooltip { display: none; position: absolute; left: 52px; top: 50%; transform: translateY(-50%); background: #1e2028; color: #fff; font-size: 12px; padding: 5px 12px; border-radius: 6px; white-space: nowrap; z-index: 100; border: 1px solid rgba(255,255,255,0.12); pointer-events: none; box-shadow: 0 4px 12px rgba(0,0,0,0.5); }
        @media (min-width: 1024px) {
          .apcs-sidebar { position: relative; transform: translateX(0) !important; width: 240px; flex-shrink: 0; z-index: auto; height: 100vh; }
          .apcs-sidebar.collapsed { width: 64px; overflow: hidden; }
          .apcs-sidebar.collapsed .nav-label, .apcs-sidebar.collapsed .group-label, .apcs-sidebar.collapsed .user-info { opacity: 0; width: 0; overflow: hidden; }
          .apcs-sidebar.collapsed .nav-btn:hover .nav-tooltip { display: block; }
          .apcs-sidebar.collapsed nav { overflow-x: hidden !important; overflow-y: auto !important; scrollbar-width: none; }
          .apcs-sidebar.collapsed nav::-webkit-scrollbar { display: none; }
          .apcs-sidebar.open + .apcs-overlay { display: none !important; }
          .apcs-main { margin-left: 0; }
          .apcs-mobile-topbar { display: none !important; }
          .apcs-desktop-topbar { display: flex !important; }
        }
      `}</style>
      {isMobilePage(page) ? (
        <div style={{ fontFamily: "'DM Sans', sans-serif", background: "#0a0c10", minHeight: "100vh" }}>
          <AppRouter page={page} navigateTo={navigateTo} selectedProject={selectedProject} setSelectedProject={setSelectedProject} currentRole={currentRole} currentRoleLabel={currentRoleLabel} canAccess={canAccess} setPage={setPage} />
        </div>
      ) : (
        <div className="apcs-root">
          <Sidebar
            sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
            page={page} navigateTo={navigateTo}
            selectedProject={selectedProject} setSelectedProject={setSelectedProject}
            canAccess={canAccess}
            currentRole={currentRole} currentUser={currentUser} currentRoleLabel={currentRoleLabel}
            data={data}
            setIsLoggedIn={setIsLoggedIn}
          />
          <div className="apcs-overlay" onClick={() => setSidebarOpen(false)} />
          <main className="apcs-main">
            <TopBar
              isMobile={isMobile}
              canGoBack={canGoBack}
              canGoForward={canGoForward}
              onBack={goBack}
              onForward={goForward}
              currentUser={currentUser}
              currentCompany={currentCompany}
              onLogout={handleSignOut}
              onOpenSidebar={() => setSidebarOpen(true)}
              onNavigate={navigateTo}
              notifications={data?.notifications}
              todos={data?.todos}
              currentUserId={currentUserId}
              currentRole={currentRole}
              users={data?.users}
              onMarkRead={markNotifRead}
              onMarkAllRead={markAllRead}
              onToggleTodo={toggleTodoStatus}
              onAddTodo={addTodo}
              onDeleteTodo={deleteTodo}
              userNotes={data?.userNotes}
              onSaveNote={saveNote}
              onDeleteNote={deleteNote}
              theme={theme}
              onToggleTheme={toggleTheme}
            />
            <div style={{ padding: 24, maxWidth: 1280, margin: "0 auto" }}>
              {["controlcenter","supervisorcc","budgetcost","boqtracking","tasks","daily","variations","procurement","warehouse","equipment","subcontractors","rfi","documents","hse","sitephotos","projectteam","evm","approvals"].includes(page) && (
                <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, padding:"10px 16px", background:"rgba(14,165,233,0.06)", border:"1px solid rgba(14,165,233,0.15)", borderRadius:10 }}>
                  <FolderKanban size={15} style={{ color:"#38bdf8", flexShrink:0 }} />
                  <span style={{ fontSize:12, color:"rgba(255,255,255,0.5)", flexShrink:0 }}>Active Project:</span>
                  <select className={inputClass} style={{ maxWidth:280, padding:"6px 10px", fontSize:13, background:"rgba(255,255,255,0.08)" }} value={selectedProject || ""} onChange={e => setSelectedProject(e.target.value)}>
                    <option value="">Select a project...</option>
                    {(currentRole === "pm" ? data.projects.filter(p => p.project_manager_id === currentUser.user_id && p.status !== "Closed") : currentRole === "supervisor" ? data.projects.filter(p => p.supervisor_id === currentUser.user_id && p.status !== "Closed") : data.projects.filter(p => p.status !== "Closed")).map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                  </select>
                  {selectedProject && (() => { const p = data.projects.find(pr => pr.project_id === selectedProject); return p ? <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>{p.client} · <StatusBadge status={p.status} /></span> : null; })()}
                </div>
              )}
              <AppRouter page={page} navigateTo={navigateTo} selectedProject={selectedProject} setSelectedProject={setSelectedProject} currentRole={currentRole} currentRoleLabel={currentRoleLabel} canAccess={canAccess} setPage={setPage} />
            </div>
          </main>
        </div>
      )}
      </ErrorBoundary>
    </DataContext.Provider>
    </ToastProvider>
  );
}
